<template lang="pug">
v-app#app(:class="$route.name === 'chatWidget' && $style.transparent", flat, fluid)
  overlay(v-if="overlay")
  v-alert.alert-success(type="info" icon="check_circle",
    dismissible, v-model="isVisibilityAlert") {{ notificationText }}
  router-view
  debug-window(v-if="showModal", @close="showModal = false")
  div(:class="[$style.notifications, isOperator && $style.notificationsOperator, isOperatorMiniMode && $style.notificationsOperatorMini]")
    notification(v-for="notification in notificationList", :data="notification",
      :key="notification.type + notification.date")
</template>

<script>
import {
  mapActions, mapState,
} from 'vuex';
import { consts, auth } from 'src/utils';
import { eventBus } from 'src/features';
import Overlay from 'components/Overlay';
import Notification from 'components/Notification';
import DebugWindow from 'src/app/DebugWindow';
import { prefix } from 'src/config';
import { C_SYSTEM_INFO_VISIBLE } from 'src/store/modules/app';

const isFrame = window !== window.top;

export default {
  name: 'app',
  components: {
    DebugWindow,
    Overlay,
    Notification,
  },
  data() {
    return {
      height: 0,
      focused: true,
      visibility: true,
      notificationText: '',
      isVisibilityAlert: false,
      showModal: false,
      urlEmojiPack: `url(${prefix}/images/emoji_pack.png)`,
    };
  },
  created() {
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('storage', (e) => {
      Object.keys(auth.tokenType).forEach((key) => {
        if (e.key === auth.tokenType[key] && !e.newValue && e.key !== auth.tokenType.JWT_CUSTOMER) {
          const token = auth.getToken(e.key);
          if (!token || e.key === auth.tokenType.JWT_OPERATOR) {
            eventBus.$emit(consts.LOGOUT_BY_ROLE);
          }
        }
      });
    });
    this.fetchConfig();
  },
  mounted() {
    this.setHeight();
    window.addEventListener('resize', this.setHeight);
    window.addEventListener('focus', this.setFocused);
    window.addEventListener('blur', this.setFocused);
    document.addEventListener('click', () => {
      this.focused = true;
    });
    document.addEventListener('visibilitychange', this.setVisibility);
    eventBus.$on(consts.ALERT, (text) => {
      this.isVisibilityAlert = true;
      this.notificationText = text;
      setTimeout(() => {
        this.isVisibilityAlert = false;
      }, 10000);
    });
    this.changeIframe(isFrame);
    if (isFrame) {
      window.addEventListener('message', (e) => {
        const json = `${e.data}`;
        if (json.indexOf('DOCUMENT_TITLE') > -1) {
          const { data: documentTitle } = JSON.parse(json);
          this.setOldDocumentTitle(documentTitle);
        }
      });
      window.parent.postMessage(JSON.stringify({ type: 'GET_DOCUMENT_TITLE' }), '*');
      if (!this.mode) this.setViewMode('FULL');
      this.getUcceIntegration().then(() => {
        if (!this.ucceEnabled) { // блокирование интеграции с реактом, если подключено ucce
          window.addEventListener('message', (e) => {
            const json = `${e.data}`;
            if (json.indexOf('GET_VIEW_MODE') > -1) {
              const { payload } = JSON.parse(json);
              this.setViewMode(payload.mode);
            }
          });
        }
      });
    } else {
      this.setOldDocumentTitle(document.title);
      this.setOldDocumentTitleHome(document.title);
    }
  },
  computed: {
    ...mapState('app', ['mode']),
    ...mapState('home', ['user', 'ucceIntegration']),
    overlay() {
      return this.$store.state.overlay.component;
    },
    notificationList() {
      return this.$store.state.notification.list;
    },
    version() {
      return process.env.VUE_APP_VERSION;
    },
    ucceEnabled() {
      return this.ucceIntegration;
    },
    isOperator() {
      return this.user?.role === 'operator';
    },
    isOperatorMiniMode() {
      // без isFrame, тк иначе ошибка при вычислении
      return this.isOperator && this.mode === 'MINI' && !this.ucceEnabled;
    },
  },
  methods: {
    ...mapActions('demo', ['setOldDocumentTitle']),
    ...mapActions('home', ['setOldDocumentTitleHome']),
    ...mapActions('home', ['getUcceIntegration']),
    ...mapActions('app', ['fetchConfig', 'changeIframe', 'setViewMode']),
    onKeyUp({
      ctrlKey,
      altKey,
      code,
      keyCode,
    }) {
      const debugKey = {
        keyCode: 76,
        code: 'KeyL',
      };
      const codeEvent = code ? code === debugKey.code : keyCode === debugKey.keyCode;
      if (ctrlKey && altKey && codeEvent && this.$store.state.app[C_SYSTEM_INFO_VISIBLE]) {
        this.showModal = !this.showModal;
      }
    },
    setHeight() {
      this.height = window.innerHeight;
    },
    setFocused() {
      this.focused = document.hasFocus();
    },
    setVisibility() {
      this.visibility = !document.hidden;
    },
  },
};
</script>

<style lang="sass">
@import '~assets/main'
@import '~src/fonts/index.css'
@import '~src/style/normalize.css'
@import '~src/style/mdi/materialdesignicons.css'
html,
input,
textarea,
button,
select
  font-family: Roboto, sans-serif
  -webkit-font-smoothing: antialiased
  -moz-osx-font-smoothing: grayscale

html
  overflow: auto

body
  height: auto !important
  color: $color_text
  min-height: 100vh !important
  font-size: 14px
  font-kerning: normal

#app
  min-height: 100vh !important
  width: 100%
  display: flex
  align-items: stretch
  flex-direction: column

.stretch-width
  width: 100%
.stretch-height
  height: 100%
.stretch
  @extend .stretch-height
  @extend .stretch-width

.alert-success
  background-color: $color_alert !important
  margin: 0px auto -65px !important
  z-index: 2000
  position: relative
  min-height: 65px
  align-items: center
  .alert__dismissible
    align-self: center
.vuetifyTooltip
  opacity: 1 !important
  width: 180px
  font-size: 11px
  line-height: 14px
  background-color: $color_tooltip_bg
  color: $color_text_medium_grey
  box-shadow: 0px 0px 8px -2px rgba(21, 23, 24, 0.2)
  border-radius: 1px !important
  padding: 5px 7px !important
  overflow-wrap: break-word !important
  word-wrap: break-word !important
  word-break: break-all !important
  word-break: break-word !important

.emoji-type-image.emoji-set-twitter
  background-image: v-bind(urlEmojiPack)
</style>

<style module lang="sass">
.transparent
  background: transparent !important

.notifications
  position: fixed
  top: 0
  bottom: auto
  right: 0
  z-index: 10000
  &Operator
    bottom: 116px
    top: auto
    &Mini
      bottom: 84px
</style>
